import React, { useState, useEffect } from 'react';
import { fetchOrganizationVendors, fetchOrganizationVendorById, fetchOrganizationVendorReportData } from '../../apiClient';

const OrganizationReportsTab = ({ organizationId }) => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  const [generatingReport, setGeneratingReport] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVendorsData = async () => {
      try {
        const { data, error } = await fetchOrganizationVendors(organizationId);

        if (error) throw error;
        setVendors(data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching vendors:', error);
        setLoading(false);
      }
    };

    fetchVendorsData();
  }, [organizationId]);

  // Generate organization-wide report for selected vendors and date range
  const generateOrganizationReport = async () => {
    try {
      setGeneratingReport(true);
      
      if (selectedVendors.length === 0) {
        alert('Please select at least one vendor for the report');
        setGeneratingReport(false);
        return;
      }
      
      // Format dates for display
      const formatDate = (date) => date.toLocaleDateString();
      
      // Create consolidated report data
      let consolidatedData = {
        totalRevenue: 0,
        totalTickets: 0,
        totalEvents: 0,
        byEventType: {},
        topEvents: [],
        weeklySales: {}
      };
      
      // Fetch data for each selected vendor
      const vendorReports = await Promise.all(
        selectedVendors.map(async (vendorId) => {
          // Get vendor data
          const { data: vendorData } = await fetchOrganizationVendorById(organizationId, vendorId);
            
          // Get custom date range report
          const { data } = await fetchOrganizationVendorReportData(organizationId, vendorId, dateRange.startDate, dateRange.endDate);
          
          if (data) {
            // Consolidate the data
            consolidatedData.totalRevenue += data.summary.totalRevenue;
            consolidatedData.totalTickets += data.summary.ticketCount;
            consolidatedData.totalEvents += data.summary.eventCount;
            
            // Combine event type data
            data.byEventType.forEach(typeData => {
              if (!consolidatedData.byEventType[typeData.type]) {
                consolidatedData.byEventType[typeData.type] = {
                  count: 0,
                  tickets: 0,
                  revenue: 0
                };
              }
              
              consolidatedData.byEventType[typeData.type].count += typeData.count;
              consolidatedData.byEventType[typeData.type].tickets += typeData.tickets;
              consolidatedData.byEventType[typeData.type].revenue += typeData.revenue;
            });
            
            // Combine top events
            data.topEvents.forEach(event => {
              consolidatedData.topEvents.push({
                ...event,
                vendorName: vendorData.company_name
              });
            });
            
            // Combine weekly sales data
            data.weeklySales.forEach(weekData => {
              if (!consolidatedData.weeklySales[weekData.week]) {
                consolidatedData.weeklySales[weekData.week] = {
                  ticketsSold: 0,
                  revenue: 0
                };
              }
              
              consolidatedData.weeklySales[weekData.week].ticketsSold += weekData.ticketsSold;
              consolidatedData.weeklySales[weekData.week].revenue += weekData.revenue;
            });
          }
          
          return {
            vendorName: vendorData.company_name,
            data
          };
        })
      );
      
      // Format the consolidated data
      const avgTicketPrice = consolidatedData.totalTickets > 0 
        ? consolidatedData.totalRevenue / consolidatedData.totalTickets 
        : 0;
        
      // Sort top events by revenue
      consolidatedData.topEvents.sort((a, b) => b.revenue - a.revenue);
      const top10Events = consolidatedData.topEvents.slice(0, 10);
      
      // Calculate top performing vendor
      const vendorPerformance = vendorReports.map(report => ({
        name: report.vendorName,
        revenue: report.data?.summary.totalRevenue || 0,
        tickets: report.data?.summary.ticketCount || 0
      })).sort((a, b) => b.revenue - a.revenue);
      
      const topVendor = vendorPerformance.length > 0 ? vendorPerformance[0] : { name: 'None', revenue: 0 };
      
      // Convert event types object to array
      const eventTypesArray = Object.entries(consolidatedData.byEventType).map(([type, data]) => ({
        type,
        ...data
      }));
      
      // Convert weekly sales object to array
      const weeklySalesArray = Object.entries(consolidatedData.weeklySales).map(([week, data]) => ({
        week,
        ...data
      }));
      
      // Format currency
      const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
      
      // Create printable HTML
      const reportHTML = `
        <html>
          <head>
            <title>Organization Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 30px; }
              h1, h2, h3 { color: #333; }
              .header { margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
              .metrics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-bottom: 25px; }
              .metric-card { background-color: #f9f9f9; padding: 15px; border-radius: 5px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
              .metric-value { font-size: 24px; font-weight: bold; color: #2563eb; margin: 5px 0; }
              .metric-label { font-size: 14px; color: #666; }
              table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
              th { background-color: #f2f2f2; }
              .footer { margin-top: 30px; font-size: 12px; color: #666; }
              .section { margin-bottom: 25px; }
              .section-title { border-bottom: 1px solid #eee; padding-bottom: 5px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Organization Performance Report</h1>
              <p>Period: ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}</p>
              <p>Generated on: ${new Date().toLocaleString()}</p>
              <p>Vendors included: ${selectedVendors.length} vendor(s)</p>
            </div>
            
            <div class="section">
              <h2 class="section-title">Performance Overview</h2>
              <div class="metrics-grid">
                <div class="metric-card">
                  <div class="metric-label">Total Revenue</div>
                  <div class="metric-value">${formatCurrency(consolidatedData.totalRevenue)}</div>
                </div>
                
                <div class="metric-card">
                  <div class="metric-label">Tickets Sold</div>
                  <div class="metric-value">${consolidatedData.totalTickets}</div>
                </div>
                
                <div class="metric-card">
                  <div class="metric-label">Number of Events</div>
                  <div class="metric-value">${consolidatedData.totalEvents}</div>
                </div>
                
                <div class="metric-card">
                  <div class="metric-label">Top Performing Vendor</div>
                  <div class="metric-value">${topVendor.name}</div>
                  <div class="text-sm text-gray-600">${formatCurrency(topVendor.revenue)}</div>
                </div>
              </div>
            </div>

            <div class="section">
              <h2 class="section-title">Vendor Performance</h2>
              <table>
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Events</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${vendorReports.map(report => `
                    <tr>
                      <td>${report.vendorName}</td>
                      <td>${report.data?.summary.eventCount || 0}</td>
                      <td>${report.data?.summary.ticketCount || 0}</td>
                      <td>${formatCurrency(report.data?.summary.totalRevenue || 0)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            
            
            <div class="section">
              <h2 class="section-title">Performance by Event Type</h2>
              <table>
                <thead>
                  <tr>
                    <th>Event Type</th>
                    <th>Number of Events</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${eventTypesArray.map(type => `
                    <tr>
                      <td>${type.type || 'Uncategorized'}</td>
                      <td>${type.count}</td>
                      <td>${type.tickets}</td>
                      <td>${formatCurrency(type.revenue)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="section">
              <h2 class="section-title">Sales Trends</h2>
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${weeklySalesArray.map(week => `
                    <tr>
                      <td>${week.week}</td>
                      <td>${week.ticketsSold}</td>
                      <td>${formatCurrency(week.revenue)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2 class="section-title">Top Performing Events</h2>
              <table>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Vendor</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${top10Events.map(event => `
                    <tr>
                      <td>${event.name}</td>
                      <td>${event.vendorName || 'Unknown'}</td>
                      <td>${event.ticketsSold}</td>
                      <td>${formatCurrency(event.revenue)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            
            <div class="footer">
              <p>Report generated by Valley Tickets Dashboard. All times are in your local timezone.</p>
            </div>
          </body>
        </html>
      `;
      
      // Open a new window and print
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(reportHTML);
      reportWindow.document.close();
      
      // Reset generating state immediately after opening the window
      setGeneratingReport(false);
      
      // Add a small delay before printing to ensure the content is loaded
      setTimeout(() => {
        reportWindow.print();
      }, 500);
      
    } catch (error) {
      console.error('Error generating organization report:', error);
      alert('Failed to generate report: ' + error.message);
      setGeneratingReport(false);
    }
  };


  if (loading) {
    return <div className="text-center py-4">Loading vendors...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-6">Generate Reports</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Date Range Selector */}
        <div>
          <h3 className="text-lg font-medium mb-4">Select Date Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input 
                type="date" 
                value={dateRange.startDate.toISOString().split('T')[0]}
                onChange={(e) => setDateRange({
                  ...dateRange,
                  startDate: new Date(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input 
                type="date" 
                value={dateRange.endDate.toISOString().split('T')[0]}
                onChange={(e) => setDateRange({
                  ...dateRange,
                  endDate: new Date(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="mt-4">
            <button
              onClick={() => {
                // Set to current month
                const now = new Date();
                setDateRange({
                  startDate: new Date(now.getFullYear(), now.getMonth(), 1),
                  endDate: new Date()
                });
              }}
              className="px-3 py-1 mr-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
            >
              Current Month
            </button>
            <button
              onClick={() => {
                // Set to previous month
                const now = new Date();
                setDateRange({
                  startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
                  endDate: new Date(now.getFullYear(), now.getMonth(), 0)
                });
              }}
              className="px-3 py-1 mr-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
            >
              Last Month
            </button>
            <button
              onClick={() => {
                // Set to last quarter
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentQuarter = Math.floor(currentMonth / 3);
                
                setDateRange({
                  startDate: new Date(now.getFullYear(), currentQuarter * 3, 1),
                  endDate: new Date(now.getFullYear(), (currentQuarter * 3) + 3, 0)
                });
              }}
              className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
            >
              Current Quarter
            </button>
          </div>
        </div>
        
        {/* Vendor Selector */}
        <div>
          <h3 className="text-lg font-medium mb-4">Select Vendors</h3>
          <div className="mb-3">
            <button
              onClick={() => setSelectedVendors(vendors.map(v => v.id))}
              className="px-3 py-1 mr-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
            >
              Select All
            </button>
            <button
              onClick={() => setSelectedVendors([])}
              className="px-3 py-1 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
            >
              Clear All
            </button>
          </div>
          
          <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-3">
            {vendors.map(vendor => (
              <div key={vendor.id} className="mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedVendors.includes(vendor.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedVendors([...selectedVendors, vendor.id]);
                      } else {
                        setSelectedVendors(selectedVendors.filter(id => id !== vendor.id));
                      }
                    }}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2">{vendor.company_name}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Generate Report Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={generateOrganizationReport}
          disabled={generatingReport || selectedVendors.length === 0}
          className={`px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            (generatingReport || selectedVendors.length === 0) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {generatingReport ? 'Generating...' : 'Generate Report'}
        </button>
      </div>
    </div>
  );
};

export default OrganizationReportsTab; 